/**
 * 附件上传组件
 * 支持拖拽上传、多文件上传、文件预览和删除
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Button, message, Space, Tag, Popconfirm } from 'antd';
import { UploadOutlined, DeleteOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import { formatFileSize, getFileIcon, getFileTypeColor, validateFileType, validateFileSize } from '../utils/fileUtils';
import {
  uploadTrainingInfoAttachments,
  getTrainingInfoAttachments,
  deleteTrainingInfoAttachment,
  downloadTrainingInfoAttachment
} from '../services/api';

const { Dragger } = Upload;

interface AttachmentUploaderProps {
  value?: any[];
  onChange?: (files: any[]) => void;
  trainingInfoId?: number;  // 编辑时传入
  maxCount?: number;         // 最大文件数，默认20
  maxSize?: number;          // 最大文件大小（字节），默认50MB
  disabled?: boolean;        // 是否禁用
  onFilesChange?: (files: File[]) => void;  // 新增：文件列表变化回调（用于新增模式）
}

const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  value = [],
  onChange,
  trainingInfoId,
  maxCount = 20,
  maxSize = 50 * 1024 * 1024,
  disabled = false,
  onFilesChange
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);

  // 加载附件列表
  const loadAttachments = useCallback(async () => {
    if (!trainingInfoId) return;
    
    try {
      const data = await getTrainingInfoAttachments(trainingInfoId);
      setAttachments(data || []);
    } catch (error) {
      console.error('加载附件列表失败:', error);
      message.error('加载附件列表失败');
    }
  }, [trainingInfoId]);

  // 加载已有附件（编辑模式）
  useEffect(() => {
    if (trainingInfoId) {
      loadAttachments();
    }
  }, [trainingInfoId, loadAttachments]);

  // 通知父组件文件列表变化（新增模式）
  useEffect(() => {
    if (!trainingInfoId && onFilesChange) {
      const files = fileList.map(file => file.originFileObj as File).filter(Boolean);
      onFilesChange(files);
    }
  }, [fileList, trainingInfoId, onFilesChange]);

  // 文件上传前的验证
  const beforeUpload = (file: File) => {
    // 验证文件类型
    if (!validateFileType(file.name)) {
      message.error(`${file.name}: 不支持的文件类型。支持 Excel、Word、PDF、压缩包、图片`);
      return Upload.LIST_IGNORE;
    }

    // 验证文件大小
    if (!validateFileSize(file.size, maxSize)) {
      message.error(`${file.name}: 文件大小超过限制（最大 ${formatFileSize(maxSize)}）`);
      return Upload.LIST_IGNORE;
    }

    // 验证文件数量
    const currentCount = attachments.length + fileList.length;
    if (currentCount >= maxCount) {
      message.error(`附件数量超过限制（最多 ${maxCount} 个）`);
      return Upload.LIST_IGNORE;
    }

    return false; // 阻止自动上传
  };

  // 手动上传文件
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('请先选择文件');
      return;
    }

    if (!trainingInfoId) {
      message.info('文件将在创建培训信息后自动上传');
      return;
    }

    setUploading(true);

    try {
      const files = fileList.map(file => file.originFileObj as File);
      const uploadedFiles = await uploadTrainingInfoAttachments(trainingInfoId, files);
      
      message.success(`成功上传 ${uploadedFiles.length} 个文件`);
      
      // 清空待上传列表
      setFileList([]);
      
      // 重新加载附件列表
      await loadAttachments();
      
      // 通知父组件
      if (onChange) {
        onChange([...attachments, ...uploadedFiles]);
      }
    } catch (error: any) {
      console.error('上传失败:', error);
      message.error(error.response?.data?.message || '文件上传失败');
    } finally {
      setUploading(false);
    }
  };

  // 删除附件
  const handleDelete = async (attachmentId: number) => {
    try {
      await deleteTrainingInfoAttachment(attachmentId);
      message.success('附件删除成功');
      
      // 重新加载附件列表
      await loadAttachments();
      
      // 通知父组件
      if (onChange) {
        const newAttachments = attachments.filter(a => a.id !== attachmentId);
        onChange(newAttachments);
      }
    } catch (error: any) {
      console.error('删除失败:', error);
      message.error(error.response?.data?.message || '删除附件失败');
    }
  };

  // 下载附件
  const handleDownload = async (attachmentId: number, originalName: string) => {
    try {
      await downloadTrainingInfoAttachment(attachmentId, originalName);
      message.success('下载成功');
    } catch (error: any) {
      console.error('下载失败:', error);
      message.error(error.response?.data?.message || '下载附件失败');
    }
  };

  // 移除待上传文件
  const handleRemove = (file: UploadFile) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
  };

  const uploadProps: UploadProps = {
    multiple: true,
    fileList,
    beforeUpload,
    onRemove: handleRemove,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    disabled: disabled || uploading
  };

  return (
    <div>
      {/* 拖拽上传区域 */}
      <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
        <p className="ant-upload-hint">
          支持 Excel、Word、PDF、压缩包、图片等格式，单个文件不超过 {formatFileSize(maxSize)}
        </p>
      </Dragger>

      {/* 上传按钮 */}
      {fileList.length > 0 && trainingInfoId && (
        <Button
          type="primary"
          onClick={handleUpload}
          loading={uploading}
          disabled={disabled}
          icon={<UploadOutlined />}
          style={{ marginBottom: 16 }}
        >
          {uploading ? '上传中...' : `上传 ${fileList.length} 个文件`}
        </Button>
      )}

      {/* 新增模式提示 */}
      {fileList.length > 0 && !trainingInfoId && (
        <div style={{ marginBottom: 16, color: '#1890ff', fontSize: '14px' }}>
          已选择 {fileList.length} 个文件，将在创建培训信息后自动上传
        </div>
      )}

      {/* 已上传的附件列表 */}
      {attachments.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            已上传附件 ({attachments.length}/{maxCount})
          </div>
          <Space direction="vertical" style={{ width: '100%' }}>
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa'
                }}
              >
                <Space>
                  <Tag color={getFileTypeColor(attachment.original_name)}>
                    {attachment.file_type}
                  </Tag>
                  <span>{attachment.original_name}</span>
                  <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                    {formatFileSize(attachment.file_size)}
                  </span>
                </Space>
                <Space>
                  <Button
                    type="link"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(attachment.id, attachment.original_name)}
                  >
                    下载
                  </Button>
                  {!disabled && (
                    <Popconfirm
                      title="确定要删除这个附件吗？"
                      onConfirm={() => handleDelete(attachment.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                      >
                        删除
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </div>
            ))}
          </Space>
        </div>
      )}

      {/* 提示信息 */}
      {attachments.length === 0 && fileList.length === 0 && (
        <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 8 }}>
          暂无附件
        </div>
      )}
    </div>
  );
};

export default AttachmentUploader;
